import { Circle, FabricText, Group, Line, Polygon, Polyline, Rect, Textbox } from 'fabric';
import type { FabricObject } from 'fabric';
import { applyFill } from '../fill';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type {
  ContentSourceSpec,
  FillValue,
  StandardContentPayload,
  ToolRecipe,
} from '../types';

/**
 * Misconception card — a "Watch out!" card that names a common mix-up and
 * corrects it: an alert header, the mistaken idea (red ✗ block), the correction
 * (green ✓ block) and an optional worked example. Excellent fully manual and
 * Smart-fillable from a standard's misconceptions + examples.
 *
 * The warning / ✗ / ✓ marks are DRAWN from stock shapes (triangle + bar + dot,
 * crossed lines, tick polyline) — no emoji-font dependency, so the card renders
 * identically on any machine. Params arrive untrusted; generate() coerces every
 * read and never throws.
 */

function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}
function rawStr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}
function solidFromFill(fill: unknown, fallback: string): string {
  if (typeof fill === 'string') return fill.trim() || fallback;
  if (fill && typeof fill === 'object') {
    const f = fill as { type?: string; color?: string; from?: string; stops?: { color?: string }[] };
    if (f.type === 'solid' && typeof f.color === 'string') return f.color;
    if (f.type === 'linear-gradient' || f.type === 'radial-gradient') {
      if (Array.isArray(f.stops) && f.stops.length && f.stops[0].color) return f.stops[0].color;
      if (typeof f.from === 'string') return f.from;
    }
  }
  return fallback;
}
function readableInkOn(bg: string, ink: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(bg).trim());
  if (!m) return ink;
  const n = parseInt(m[1], 16);
  const L = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return L > 0.6 ? ink : '#ffffff';
}

const INK = '#0f172a';
const DEFAULT_FONT = 'Nunito';
// Semantic marks (wrong = red, right = green) — fixed so meaning never depends
// on the themeable fills.
const MISS_BG = '#fee2e2';
const X_RED = '#dc2626';
const CHECK_GREEN = '#16a34a';
const EX_BG = '#f1f5f9';

const SECTION_CONTENT = 'Content';
const SECTION_STYLE = 'Style';
const SECTION_COLORS = 'Colors';
const SECTION_FONTS = 'Fonts';

// ---------------------------------------------------------------------------
// Drawn marks.
// ---------------------------------------------------------------------------

function warningBadge(cx: number, cy: number, size: number, triFill: string, markFill: string): FabricObject[] {
  const hw = size * 0.6;
  const hh = size * 0.52;
  const tri = new Polygon(
    [
      { x: cx, y: cy - hh },
      { x: cx - hw, y: cy + hh },
      { x: cx + hw, y: cy + hh },
    ],
    { fill: triFill, stroke: triFill, strokeWidth: 2, strokeLineJoin: 'round' },
  );
  const bar = new Rect({
    left: cx,
    top: cy - size * 0.02,
    originX: 'center',
    originY: 'center',
    width: size * 0.14,
    height: size * 0.4,
    rx: size * 0.06,
    ry: size * 0.06,
    fill: markFill,
  });
  const dot = new Circle({ left: cx, top: cy + size * 0.34, originX: 'center', originY: 'center', radius: size * 0.08, fill: markFill });
  return [tri, bar, dot];
}

function xBadge(cx: number, cy: number, r: number): FabricObject[] {
  const c = new Circle({ left: cx, top: cy, originX: 'center', originY: 'center', radius: r, fill: X_RED });
  const d = r * 0.44;
  const line = (x1: number, y1: number, x2: number, y2: number) =>
    new Line([x1, y1, x2, y2], { stroke: '#ffffff', strokeWidth: Math.max(3, r * 0.16), strokeLineCap: 'round' });
  return [c, line(cx - d, cy - d, cx + d, cy + d), line(cx - d, cy + d, cx + d, cy - d)];
}

function checkBadge(cx: number, cy: number, r: number): FabricObject[] {
  const c = new Circle({ left: cx, top: cy, originX: 'center', originY: 'center', radius: r, fill: CHECK_GREEN });
  const tick = new Polyline(
    [
      { x: cx - r * 0.42, y: cy + r * 0.04 },
      { x: cx - r * 0.1, y: cy + r * 0.38 },
      { x: cx + r * 0.46, y: cy - r * 0.34 },
    ],
    { fill: '', stroke: '#ffffff', strokeWidth: Math.max(3, r * 0.18), strokeLineCap: 'round', strokeLineJoin: 'round' },
  );
  return [c, tick];
}

const contentSource: ContentSourceSpec = {
  kind: 'standard',
  wants: ['misconceptions', 'examples'],
  applyContent(payload: StandardContentPayload, current: Record<string, unknown>) {
    const next = { ...current };
    const m = Array.isArray(payload?.misconceptions) ? payload.misconceptions[0] : undefined;
    if (m) {
      if (m.text) next.misconception = m.text;
      // Clear the sample fields when the standard's misconception carries none —
      // a real mix-up beside a sample fraction correction/example is worse than
      // a blank (owner 2026-07-24). The example strip is omitted on blank.
      next.correction = m.correction || '';
      const ex = Array.isArray(payload?.examples) ? payload.examples[0] : undefined;
      next.example = ex?.text || '';
    }
    if (payload?.standard?.code) next.sourceStandardCode = payload.standard.code;
    if (payload?.standard?.id) next.sourceStandardId = payload.standard.id;
    return next;
  },
};

const misconceptionCard: ToolRecipe = {
  id: 'misconception-card',
  domain: 'display',
  category: 'decor',
  tags: ['misconception', 'error-analysis', 'watch-out', 'common-mistake', 'anchor', 'standards'],
  name: 'Misconception Card',
  version: 1,
  description:
    'A "Watch out!" card that names a common mix-up and shows the correct thinking — with an optional worked example. Type your own or pull it from a standard.',
  defaults: {
    heading: 'Common mix-up!',
    misconception: 'A bigger denominator always means a bigger fraction, so 1/8 is greater than 1/4.',
    correction: 'The more equal parts you cut a whole into, the smaller each part is — so 1/8 < 1/4. Eighths are smaller pieces than fourths.',
    example: 'Cut one pizza into 8 slices and another into 4: the 8-slice pieces are smaller.',
    style: 'alert',
    alertFill: '#ef4444',
    fixFill: '#dcfce7',
    textColor: INK,
    textFont: DEFAULT_FONT,
    headingFont: '',
    bodyFont: '',
    sourceStandardCode: '',
    sourceStandardId: '',
  },
  schema: [
    { key: 'heading', label: 'Heading', type: 'text', section: SECTION_CONTENT },
    { key: 'misconception', label: 'The mix-up', type: 'multiline', section: SECTION_CONTENT, help: 'The mistaken idea students often have.' },
    { key: 'correction', label: 'The fix', type: 'multiline', section: SECTION_CONTENT, help: 'The correct thinking.' },
    { key: 'example', label: 'Example', type: 'multiline', section: SECTION_CONTENT, help: 'Optional — a strip is added only when this is filled.' },
    {
      key: 'style',
      label: 'Card style',
      type: 'select',
      section: SECTION_STYLE,
      options: [
        { value: 'alert', label: 'Alert' },
        { value: 'sticky-note', label: 'Sticky note' },
        { value: 'speech', label: 'Speech bubble' },
      ],
    },
    { key: 'alertFill', label: 'Header fill', type: 'fill', section: SECTION_COLORS, help: 'Solid, gradient, or texture.' },
    { key: 'fixFill', label: 'Correction fill', type: 'fill', section: SECTION_COLORS, help: 'Behind the corrected idea.' },
    { key: 'textColor', label: 'Text color', type: 'color', section: SECTION_COLORS },
    { key: 'textFont', label: 'Text font (default for all parts)', type: 'font', section: SECTION_FONTS },
    { key: 'headingFont', label: 'Heading font', type: 'font', section: SECTION_FONTS },
    { key: 'bodyFont', label: 'Body font', type: 'font', section: SECTION_FONTS },
    { key: 'sourceStandardCode', label: 'Source standard code', type: 'text', section: SECTION_CONTENT, advanced: true, help: 'Set automatically when filled from a standard.' },
    { key: 'sourceStandardId', label: 'Source standard id', type: 'text', section: SECTION_CONTENT, advanced: true, help: 'Set automatically when filled from a standard.' },
  ],
  modal: {
    hero: ['heading', 'misconception', 'correction'],
    columns: 2,
    sectionOrder: [SECTION_CONTENT, SECTION_STYLE, SECTION_COLORS, SECTION_FONTS],
  },
  gradeBands: {
    K2: { textFont: 'Fredoka', style: 'sticky-note' },
    G35: { textFont: 'Nunito', style: 'sticky-note' },
    G68: { textFont: 'Lexend', style: 'alert' },
    G912: { textFont: 'Arial', style: 'alert' },
  },
  contentSource,
  async generate(params) {
    const W = 640;
    const pad = 28;
    const blockPad = 20;
    const badgeGutter = 52;

    const styleRaw = str(params.style, 'alert');
    const style = styleRaw === 'sticky-note' || styleRaw === 'speech' ? styleRaw : 'alert';
    const rx = style === 'sticky-note' ? 6 : style === 'speech' ? 28 : 24;

    const textColor = str(params.textColor, INK);
    const textFont = str(params.textFont, DEFAULT_FONT);
    const partFont = (key: string): string => {
      const v = params[key];
      return typeof v === 'string' && v.trim() !== '' ? v : textFont;
    };
    const headingFont = partFont('headingFont');
    const bodyFont = partFont('bodyFont');

    const alertFill = (params.alertFill ?? '#ef4444') as FillValue;
    const fixFill = (params.fixFill ?? '#dcfce7') as FillValue;
    const bandSolid = solidFromFill(alertFill, '#ef4444');
    const headingInk = readableInkOn(bandSolid, INK);

    const heading = str(params.heading, 'Common mix-up!');
    const misconception = rawStr(params.misconception, '').trim();
    const correction = rawStr(params.correction, '').trim();
    const example = rawStr(params.example, '').trim();

    const cardX = pad;
    const cardW = W - pad * 2;
    const bandH = 64;
    const textLeft = cardX + blockPad + badgeGutter;
    const blockTextW = Math.max(40, cardW - blockPad * 2 - badgeGutter);

    // Objects are staged; the backdrop (which needs the final H) is unshifted
    // in at the end, so height can be computed as the stack is built.
    const body: FabricObject[] = [];
    const fillJobs: { obj: FabricObject; fill: FillValue; fallback: string }[] = [];

    // Header band + heading + warning badge.
    let y = pad;
    const band = new Rect({ left: cardX, top: y, width: cardW, height: bandH, rx: 16, ry: 16 });
    fillJobs.push({ obj: band, fill: alertFill, fallback: '#ef4444' });
    body.push(band);
    const badgeCx = cardX + blockPad + 20;
    body.push(...warningBadge(badgeCx, y + bandH / 2, 22, headingInk, bandSolid));
    body.push(
      new Textbox(heading, {
        left: badgeCx + 34,
        top: y + bandH / 2,
        width: cardW - blockPad * 2 - 54,
        fontSize: 30,
        fontFamily: headingFont,
        fontWeight: 'bold',
        fill: headingInk,
        textAlign: 'left',
        originX: 'left',
        originY: 'center',
      }),
    );
    y += bandH + 16;

    // A badge block (mix-up / fix): rounded fill, badge, wrapped text.
    const addBadgeBlock = (text: string, fallbackText: string, fill: FillValue, fallback: string, badge: 'x' | 'check') => {
      const tb = new Textbox(text || fallbackText, {
        left: textLeft,
        top: 0,
        width: blockTextW,
        fontSize: 22,
        fontFamily: bodyFont,
        fill: textColor,
        textAlign: 'left',
        lineHeight: 1.2,
        originX: 'left',
        originY: 'top',
      });
      const inner = Math.max(44, tb.height ?? 22);
      const blockH = inner + blockPad * 2;
      const rect = new Rect({ left: cardX, top: y, width: cardW, height: blockH, rx: 14, ry: 14 });
      fillJobs.push({ obj: rect, fill, fallback });
      body.push(rect);
      const bcy = y + blockPad + 20;
      const bcx = cardX + blockPad + 18;
      body.push(...(badge === 'x' ? xBadge(bcx, bcy, 18) : checkBadge(bcx, bcy, 18)));
      tb.set({ top: y + blockPad });
      body.push(tb);
      y += blockH + 14;
    };

    addBadgeBlock(misconception, 'Describe the common mistake here.', MISS_BG, MISS_BG, 'x');
    addBadgeBlock(correction, 'Explain the correct thinking here.', fixFill, '#dcfce7', 'check');

    // Optional example strip (omitted entirely when blank).
    if (example) {
      const label = new FabricText('Example', {
        left: cardX + blockPad,
        top: y + blockPad,
        fontFamily: bodyFont,
        fontSize: 15,
        fontWeight: 'bold',
        fill: '#475569',
        originX: 'left',
        originY: 'top',
      });
      const tb = new Textbox(example, {
        left: cardX + blockPad,
        top: y + blockPad + 22,
        width: cardW - blockPad * 2,
        fontSize: 20,
        fontFamily: bodyFont,
        fill: textColor,
        textAlign: 'left',
        lineHeight: 1.2,
        originX: 'left',
        originY: 'top',
      });
      const blockH = 22 + (tb.height ?? 20) + blockPad * 2;
      const rect = new Rect({ left: cardX, top: y, width: cardW, height: blockH, rx: 14, ry: 14, fill: EX_BG });
      body.push(rect, label, tb);
      y += blockH + 14;
    }

    const H = y - 14 + pad;

    // Backdrop + style extras, drawn first (so they sit behind the body).
    const objects: FabricObject[] = [
      new Rect({ left: 0, top: 0, width: W, height: H, fill: 'transparent', strokeWidth: 0 }),
    ];
    const backdrop = new Rect({
      left: 0,
      top: 0,
      width: W,
      height: H,
      rx,
      ry: rx,
      fill: style === 'sticky-note' ? '#fffdf5' : '#ffffff',
      stroke: '#e2e8f0',
      strokeWidth: 2,
    });
    objects.push(backdrop);
    if (style === 'sticky-note') {
      // Folded top-right corner.
      const fold = 34;
      objects.push(
        new Polygon(
          [
            { x: W - fold, y: 0 },
            { x: W, y: fold },
            { x: W - fold, y: fold },
          ],
          { fill: '#fde68a', stroke: '#e2e8f0', strokeWidth: 1 },
        ),
      );
    } else if (style === 'speech') {
      // Tail at the bottom-left.
      objects.push(
        new Polygon(
          [
            { x: 60, y: H - 2 },
            { x: 60, y: H + 34 },
            { x: 104, y: H - 2 },
          ],
          { fill: '#ffffff', stroke: '#e2e8f0', strokeWidth: 2 },
        ),
      );
    }

    objects.push(...body);

    await Promise.all(fillJobs.map((j) => applyFill(j.obj, j.fill, j.fallback)));

    const group = new Group(objects);
    group.set({ left: 0, top: 0 });
    return tagSmartObject(group, misconceptionCard, params);
  },
};

registerRecipe(misconceptionCard);
